import { track } from '../../analytics/analytics';
import { CLASSIC_SHOP_URL } from '../../app/constants';
import { cartTotal, getCartService, notifyCartChanged } from '../../commerce/cart.service';
import { CommerceError, type CartLine } from '../../commerce/shopify.types';

export interface CartPanel {
  element: HTMLElement;
  open(): Promise<void>;
  close(): void;
  isOpen(): boolean;
  /** Redessine le contenu depuis le panier ; utile après un ajout. */
  refresh(): Promise<void>;
}

function formatMoney(amount: number, currencyCode: string): string {
  if (Number.isNaN(amount)) return '';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currencyCode || 'EUR',
  }).format(amount);
}

function lineAmount(line: CartLine): number {
  const unit = Number.parseFloat(line.price.amount);
  return Number.isNaN(unit) ? 0 : unit;
}

/**
 * Contenu du panier, dans le même registre que `.product-panel` : panneau
 * latéral sur desktop, bottom sheet sur mobile, le décor reste visible derrière.
 */
export function createCartPanel(parent: HTMLElement): CartPanel {
  const element = document.createElement('aside');
  element.className = 'cart-panel';
  element.setAttribute('role', 'dialog');
  element.setAttribute('aria-modal', 'false');
  element.setAttribute('aria-label', 'Votre panier');
  element.hidden = true;

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'cart-panel__close';
  close.setAttribute('aria-label', 'Fermer le panier');
  close.textContent = '×';

  const title = document.createElement('h2');
  title.className = 'cart-panel__title';
  title.textContent = 'Votre panier';

  const body = document.createElement('div');
  body.className = 'cart-panel__body';

  const feedback = document.createElement('p');
  feedback.className = 'cart-panel__feedback';
  feedback.setAttribute('role', 'status');
  feedback.setAttribute('aria-live', 'polite');

  element.append(close, title, body, feedback);
  parent.append(element);

  let lastFocused: HTMLElement | null = null;
  let busy = false;

  function hide(): void {
    if (element.hidden) return;
    element.hidden = true;
    element.classList.remove('is-open');
    lastFocused?.focus();
    lastFocused = null;
  }

  function show(): void {
    if (element.hidden) {
      lastFocused = document.activeElement as HTMLElement | null;
      element.hidden = false;
      requestAnimationFrame(() => element.classList.add('is-open'));
      close.focus();
    }
  }

  function say(message: string): void {
    feedback.textContent = message;
  }

  /** Toute mutation passe par ici : état occupé, redessin, compteur, message d'erreur. */
  async function mutate(action: () => Promise<void>, done: string): Promise<void> {
    if (busy) return;
    busy = true;
    element.dataset['busy'] = 'true';
    try {
      await action();
      notifyCartChanged();
      await render();
      say(done);
    } catch (error) {
      const message =
        error instanceof CommerceError
          ? error.message
          : 'Le panier n’a pas pu être modifié. La boutique classique reste accessible.';
      say(message);
    } finally {
      busy = false;
      delete element.dataset['busy'];
    }
  }

  function renderLine(line: CartLine, list: HTMLElement): void {
    const item = document.createElement('li');
    item.className = 'cart-panel__line';
    item.dataset['variantId'] = line.variantId;

    const media = document.createElement('div');
    media.className = 'cart-panel__thumb';
    if (line.imageUrl) {
      const image = document.createElement('img');
      image.src = line.imageUrl;
      image.alt = line.imageAlt || line.title;
      image.loading = 'lazy';
      // Repli visuel si l'image ne se charge pas.
      image.addEventListener('error', () => {
        image.remove();
        media.classList.add('is-empty');
      });
      media.append(image);
    } else {
      media.classList.add('is-empty');
    }

    const details = document.createElement('div');
    details.className = 'cart-panel__details';

    const name = document.createElement('p');
    name.className = 'cart-panel__line-title';
    name.textContent = line.title;

    const unit = document.createElement('p');
    unit.className = 'cart-panel__unit-price';
    unit.textContent = `${formatMoney(lineAmount(line), line.price.currencyCode)} l’unité`;

    const controls = document.createElement('div');
    controls.className = 'cart-panel__quantity';
    controls.setAttribute('role', 'group');
    controls.setAttribute('aria-label', `Quantité de ${line.title}`);

    const less = document.createElement('button');
    less.type = 'button';
    less.className = 'cart-panel__step';
    less.setAttribute('aria-label', `Retirer une unité de ${line.title}`);
    less.textContent = '−';
    less.addEventListener('click', () => {
      void mutate(
        () => getCartService().updateLine(line.variantId, line.quantity - 1),
        line.quantity - 1 < 1
          ? `${line.title} a été retiré du panier.`
          : `${line.title} : ${line.quantity - 1} au panier.`,
      );
    });

    const count = document.createElement('span');
    count.className = 'cart-panel__count';
    count.textContent = String(line.quantity);

    const more = document.createElement('button');
    more.type = 'button';
    more.className = 'cart-panel__step';
    more.setAttribute('aria-label', `Ajouter une unité de ${line.title}`);
    more.textContent = '+';
    more.addEventListener('click', () => {
      void mutate(
        () => getCartService().updateLine(line.variantId, line.quantity + 1),
        `${line.title} : ${line.quantity + 1} au panier.`,
      );
    });

    controls.append(less, count, more);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'cart-panel__remove';
    remove.setAttribute('aria-label', `Retirer ${line.title} du panier`);
    remove.textContent = 'Retirer';
    remove.addEventListener('click', () => {
      void mutate(
        () => getCartService().removeLine(line.variantId),
        `${line.title} a été retiré du panier.`,
      );
    });

    const subtotal = document.createElement('p');
    subtotal.className = 'cart-panel__subtotal';
    const subtotalAmount = lineAmount(line) * line.quantity;
    subtotal.textContent = formatMoney(subtotalAmount, line.price.currencyCode);
    subtotal.setAttribute(
      'aria-label',
      `Sous-total ${line.title} : ${formatMoney(subtotalAmount, line.price.currencyCode)}`,
    );

    const lineActions = document.createElement('div');
    lineActions.className = 'cart-panel__line-actions';
    lineActions.append(controls, remove);

    details.append(name, unit, lineActions);
    item.append(media, details, subtotal);
    list.append(item);
  }

  function renderEmpty(): void {
    // Un panier vide vaut mieux avec son objet : la page ne se réduit plus à
    // une phrase seule au milieu du blanc.
    const illustration = document.createElement('img');
    illustration.className = 'cart-panel__illustration';
    illustration.src = '/props/prop-panier.png';
    illustration.alt = '';
    illustration.decoding = 'async';

    const empty = document.createElement('p');
    empty.className = 'cart-panel__empty';
    empty.textContent =
      'Le panier est vide. Le chien prétend que ce n’est pas de sa faute.';

    const link = document.createElement('a');
    link.className = 'cart-panel__action cart-panel__action--ghost';
    link.href = CLASSIC_SHOP_URL;
    link.rel = 'noopener noreferrer';
    link.target = '_blank';
    link.textContent = 'Ouvrir la boutique classique';

    body.append(illustration, empty, link);
  }

  function renderCheckout(lines: CartLine[]): void {
    const total = cartTotal(lines);

    const totalRow = document.createElement('p');
    totalRow.className = 'cart-panel__total';
    const totalLabel = document.createElement('span');
    totalLabel.textContent = 'Total';
    const totalValue = document.createElement('span');
    totalValue.className = 'cart-panel__total-value';
    totalValue.textContent = formatMoney(total.amount, total.currencyCode);
    totalRow.append(totalLabel, totalValue);

    const actions = document.createElement('div');
    actions.className = 'cart-panel__actions';

    const checkout = document.createElement('button');
    checkout.type = 'button';
    checkout.className = 'cart-panel__action cart-panel__action--primary';
    checkout.textContent = 'Passer commande';
    checkout.addEventListener('click', () => {
      void openCheckout(checkout);
    });

    const keep = document.createElement('button');
    keep.type = 'button';
    keep.className = 'cart-panel__action cart-panel__action--ghost';
    keep.textContent = 'Continuer la visite';
    keep.addEventListener('click', hide);

    actions.append(checkout, keep);
    body.append(totalRow, actions);
  }

  async function openCheckout(button: HTMLButtonElement): Promise<void> {
    button.disabled = true;
    try {
      const url = await getCartService().getCheckoutUrl();
      track('checkout_opened', { source: 'cart-panel' });
      window.open(url, '_blank', 'noopener');
    } catch (error) {
      const message =
        error instanceof CommerceError
          ? error.message
          : 'La commande n’a pas pu s’ouvrir. La boutique classique reste accessible.';
      say(message);
    } finally {
      button.disabled = false;
    }
  }

  async function render(): Promise<void> {
    let lines: CartLine[] = [];
    try {
      lines = await getCartService().getLines();
    } catch {
      body.replaceChildren();
      say('Le panier ne répond pas. La boutique classique reste accessible.');
      renderEmpty();
      return;
    }

    body.replaceChildren();
    if (lines.length === 0) {
      renderEmpty();
      return;
    }

    const list = document.createElement('ul');
    list.className = 'cart-panel__list';
    for (const line of lines) renderLine(line, list);
    body.append(list);
    renderCheckout(lines);
  }

  close.addEventListener('click', hide);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !element.hidden) hide();
  });

  return {
    element,
    async open(): Promise<void> {
      say('');
      show();
      await render();
    },
    close: hide,
    isOpen: () => !element.hidden,
    refresh: render,
  };
}
